import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Divider, DropdownOption, DropdownSelectAllOption, Typography, Dropdown, SearchField, RadioButtonGroup, RadioButton, CheckboxGroup, Checkbox, InlineMessage, Spinner } from "@volvo/vcdk-react";
import { DropdownChangeEvent, MultiDropdownChangeEvent, TextFieldCustomEvent, CheckboxChangeEvent, RadioButtonGroupChangeEvent } from "@volvo/vcdk";
import { ClassTreeView } from './DealerCard/ClassTreeView';
import { EntityDropdown } from './DealerCard/EntityDropdown';
import { ContractDropdown } from './DealerCard/ContractDropdown';
import { useGetCountriesQuery, useLazyGetDealersQuery } from '../../store/InvoiceApiSlice';
import { Country, Dealer, AccountableType, MarketCompany } from '../../Types';

export const DealerCard = forwardRef((props, ref) => {

    useImperativeHandle(ref, () => ({
        getDealerStatus: () => ({value: refFilterParams.current.status, label: refFilterParams.current.status === '' ? 'All' : (refFilterParams.current.status === '1' ? 'Active' : 'Inactive')}),
        getSelectedCountry: () => ({id: refFilterParams.current.countryId, name: countryList.find((c: Country) => c.countryID.toString() === refFilterParams.current.countryId)?.name || ''}),
        getSelectedClasses: () => (refFilterParams.current.classType),        
        getSelectedDealers: () => {
            const dealerList = dealerData.dealerList?.filter(dealer => dealer.checked) || [];
            if(dealerList.length === 0) setValid(false);
            return dealerList;
        },
        getSelectedContracts: () => (refContractDropdown.current?.getSelectedContracts() || []),
        getUnselectedContracts: () => (refContractDropdown.current?.getUnselectedContracts() || []),
        getAllClasses: () => (refClassTreeView.current?.getAllClasses() || [])
    }));

    const [disableAll, setDisableAll] = useState<{disable: boolean, isAll: boolean | undefined}>({disable: false, isAll: undefined});
    const [allDealersChecked, setAllDealersChecked] = useState<boolean | undefined>(false);
    const [dealerStatus, setDealerStatus] = useState<string>("1");
    const [getDealerList, dealerListQuery] = useLazyGetDealersQuery();
    const [dealerData, setDealerData] = useState<{dealerList: Dealer[] | undefined, loading: boolean, msg: string }>({ dealerList: undefined, loading: false, msg: '' });
    const {data: countryList = [], isLoading: countryLoading, isSuccess: countrySuccess, isError: isCountryError, error: countryError} = useGetCountriesQuery({});
    const [marketList, setMarketList] = useState<MarketCompany[]>([]);
    const refFilterParams = useRef<{
        countryId: string, 
        marketId: string[],
        keywords: string,
        classType: Map<number, number[]>,
        status: string
    }>({countryId: '', marketId: [], keywords: '', classType: new Map<number, number[]>(), status: "1"});
    const refSelectedDealers = useRef<Map<string, Set<number>>>(new Map<string, Set<number>>());
    const refDealerData = useRef<Map<string, {data: Dealer[] | undefined, status: number, msg: string}>>(new Map<string, {data: Dealer[] | undefined, status: number, msg: string}>());
    const refContractDropdown = useRef<any>(null), refClassTreeView = useRef<any>(null);
    const [valid, setValid] = useState(true);

    useEffect(() => {
        if(dealerListQuery.originalArgs){
            const countryId = dealerListQuery.originalArgs.countryId;
            if(dealerListQuery.isSuccess) {
                refDealerData.current.set(countryId, {data: dealerListQuery.data, status: statusCompleted, msg: ''});
                if(dealerData.loading) setStateDealers();
            }else if(dealerListQuery.isError){
                refDealerData.current.set(countryId, {data: undefined, status: statusError, msg: 'Error loading dealers'});                
                setDealerData(prevState => ({ ...prevState, dealerList: undefined, loading: false, msg: 'Error loading dealers' }));
            }
        }
    }, [dealerListQuery.data, dealerListQuery.isSuccess]);

    useEffect(() => {
        if(dealerData.dealerList != undefined && dealerData.loading){
            setDealerData(prevState => ({...prevState, loading: false}));
            let checkedCount = dealerData.dealerList.filter(dealer => dealer.checked === true).length;
            setAllDealersChecked(checkedCount === dealerData.dealerList.length && checkedCount > 0 ? true : (checkedCount > 0 ? undefined : false));
        } 
        if(disableAll.disable) {
            if(disableAll.isAll === false) {
                let selectedIdSet = refSelectedDealers.current.has(refFilterParams.current.countryId) ? refSelectedDealers.current.get(refFilterParams.current.countryId) : undefined;
                setAllDealersChecked(selectedIdSet == undefined || selectedIdSet.size === 0 ? false : (selectedIdSet?.size === dealerData.dealerList?.length ? true : undefined));
            }
            setDisableAll(prevState => ({...prevState, disable: false}));
        }
    }, [dealerData]);

    useEffect(() => {
        if(!disableAll.disable) return;
        if(disableAll.isAll) {
            const selectedIdSet = refSelectedDealers.current.has(refFilterParams.current.countryId) ? refSelectedDealers.current.get(refFilterParams.current.countryId) : new Set<number>();
            refSelectedDealers.current.set(refFilterParams.current.countryId, selectedIdSet || new Set<number>());
            let checked = allDealersChecked === true ? false : true;
            setDealerData(prevState => {
                return {...prevState, dealerList: prevState.dealerList?.map(dealer => {
                    if(checked) selectedIdSet?.add(dealer.dealerID);
                    else if(checked === false && selectedIdSet && selectedIdSet.size > 0) selectedIdSet.delete(dealer.dealerID);
                    dealer.checked = checked;
                    return dealer;
                })};
            });
            setAllDealersChecked(!allDealersChecked);
        }
    }, [disableAll]);

    const onCountryChange = (e: DropdownChangeEvent) => {
        refClassTreeView.current?.selectedAllClasses(false);
        const countryId = e.detail.value;
        refFilterParams.current.countryId = countryId;
        setMarketList(countryList.find((country: Country) => country.countryID.toString() === countryId)?.marketCompanyList || []);
        let hasCachedData = refDealerData.current.has(countryId);
        setDealerData(prevState => ({...prevState, dealerList: undefined, loading: false, msg: ''}));
        if(!hasCachedData || refDealerData.current.get(countryId)?.status === statusError){
            refDealerData.current.set(countryId, {data: undefined, status: statusLoading, msg: ''});
            getDealerList({countryId: countryId});
        }
    }

    const onMarketChanged = (e: MultiDropdownChangeEvent) => {
        if(e.detail.length > 0) {
            refClassTreeView.current?.selectedAllClasses(true);
            refClassTreeView.current?.getAllClasses().forEach((dealerClass: {
                dealerClassID: number; name: string; description: string; typeList: AccountableType[]
            }) => (refFilterParams.current.classType.set(dealerClass.dealerClassID, dealerClass.typeList.map(type => type.accountableTypeID))));
        }
        refFilterParams.current.marketId = e.detail.map(market => market.value);
        const cachedData = refDealerData.current.get(refFilterParams.current.countryId);
        if(cachedData?.status === statusCompleted) {
            setStateDealers();
        }else{
            if(cachedData?.status === statusLoading) setDealerData(prevState => ({...prevState, loading: true}));
            else if(cachedData?.status === statusError) setDealerData(prevState => ({...prevState, dealerList: undefined, loading: false, msg: 'Error loading dealers'}));
        }
    }

    const setStateDealers = () => {
        let currentFilter = refFilterParams.current;
        const cachedDealers = refDealerData.current.get(currentFilter.countryId);
        const cachedSelectedDealers = refSelectedDealers.current.get(currentFilter.countryId);
        setDealerData(prevState => ({...prevState, dealerList: cachedDealers?.data?.map(dealer => {
            if(currentFilter.marketId.length === 0) return {...dealer, visible: false};
            let visible = true, checked = cachedSelectedDealers?.has(dealer.dealerID) ? true : false;
            if(!currentFilter.marketId.includes(dealer.marketCompanyID.toString())) visible = false;
            if(currentFilter.keywords !== '' && dealer.name && visible === true){
                const keyword = currentFilter.keywords.toLowerCase();
                visible = dealer.name.toLowerCase().includes(keyword) || dealer.dealerNumber.toLowerCase().includes(keyword);
            }
            if(visible === true && currentFilter.classType.size > 0 && dealer.brandClassList.length > 0){
                visible = dealer.brandClassList.some(brandClass => {
                    let typeIdArray = currentFilter.classType.get(brandClass.dealerClassID);
                    if(typeIdArray != undefined) {
                        if(typeIdArray.length === 0) return true;
                        return dealer.brandClassList.some(brandClass => typeIdArray.includes(brandClass.accountableTypeID));
                    }
                    return false;
                });
            }
            return {...dealer, visible: visible, checked: checked};
        }).filter(d => d.visible !== false).sort((a, b) => {
            if(!a.dealerNumber) return -1;
            return a.dealerNumber.localeCompare(b.dealerNumber);
        }), loading: true, msg: ''}));
    }

    const statusLoading = 0, statusCompleted = 1, statusError = 2;    

    const classChangedCallback = (classID: number, checked: boolean, typeList: AccountableType[]) => {
        if(checked && !refFilterParams.current.classType.has(classID)) refFilterParams.current.classType.set(classID, typeList.map(type => type.accountableTypeID));
        else if(!checked && refFilterParams.current.classType.has(classID)) refFilterParams.current.classType.delete(classID);
        if(dealerData.dealerList != undefined) setStateDealers();
    }

    const typeChangedCallback = (type: AccountableType, checked: boolean) => {
        if(checked) {
            if(refFilterParams.current.classType.has(type.dealerClassID)) {
                let typeIdArray = refFilterParams.current.classType.get(type.dealerClassID);
                if(!typeIdArray?.includes(type.accountableTypeID)) typeIdArray?.push(type.accountableTypeID);
                refFilterParams.current.classType.set(type.dealerClassID, typeIdArray ?? []);
            } else refFilterParams.current.classType.set(type.dealerClassID, [type.accountableTypeID]);
        } else {
            if(refFilterParams.current.classType.has(type.dealerClassID)) {
                let typeIdArray = refFilterParams.current.classType.get(type.dealerClassID)?.filter(id => id !== type.accountableTypeID);
                if(typeIdArray !== undefined && typeIdArray.length === 0) refFilterParams.current.classType.delete(type.dealerClassID);
                else if(typeIdArray !== undefined && typeIdArray.length > 0) refFilterParams.current.classType.set(type.dealerClassID, typeIdArray);
            }
        }
        if(dealerData.dealerList !== undefined) setStateDealers();
    }

    const onSearch = (e: CustomEvent<TextFieldCustomEvent>) => {
        refFilterParams.current.keywords = e.detail.value.toLowerCase();
        if(!dealerData.dealerList) return;
        setStateDealers();
    }

    const onClearSearch = () => {
        refFilterParams.current.keywords = '';
        setStateDealers();
    }

    const onDealerChanged = (e: CheckboxChangeEvent, dealerId: number) => {
        setDisableAll(prevState => ({...prevState, disable: true, isAll: false}));
        let selectedIdSet = refSelectedDealers.current.has(refFilterParams.current.countryId) ? refSelectedDealers.current.get(refFilterParams.current.countryId) : new Set<number>();
        if(e.detail.checked === true) {
            if(!valid) setValid(true);
            selectedIdSet?.add(dealerId);
        } 
        else if(e.detail.checked === false && selectedIdSet && selectedIdSet.size > 0) selectedIdSet.delete(dealerId);
        refSelectedDealers.current.set(refFilterParams.current.countryId, selectedIdSet || new Set<number>());
        setDealerData(prevState => {
            return {...prevState, dealerList: prevState.dealerList?.map(dealer => {
                if(dealer.dealerID === dealerId) dealer.checked = e.detail.checked;
                return dealer;
            })};
        });
    }
    
    return (<div style={{ border: '1px solid #E1E1E1', borderRadius: 8, padding: 20, backgroundColor: '#F7F7F7', gap: 8, height: 772, flex: 1 }}>
        <div><Typography variant='subtitle1' style={{ color: valid ? 'inherit' : '#BF2012' }}>Dealer</Typography></div>
        <div style={{ height: 700, display: 'flex', gap: 20 }}>
            <div style={{ width: 400, height: '100%', display: 'grid', gap: 10 }}>
                <div>
                    <RadioButtonGroup onGroupChange={(e: RadioButtonGroupChangeEvent) => {
                        refFilterParams.current.status = e.detail.value!;
                        setDealerStatus(e.detail.value!);
                    }} orientation="horizontal" size="medium" style={{ height: 24 }}>
                        <RadioButton value="" label="All" checked={dealerStatus === ''} />
                        <RadioButton value="1" label="Active" checked={dealerStatus === '1'} />
                        <RadioButton value="0" label="Inactive" checked={dealerStatus === '0'} />
                    </RadioButtonGroup>
                </div>
                <div>
                    {countryLoading ? <Spinner /> :
                    <Dropdown label='Country' size='small' onChange={onCountryChange}>
                        {countrySuccess && countryList.map((country: Country) => (
                            <DropdownOption key={country.countryID} value={country.countryID.toString()}>
                                {country.name}
                            </DropdownOption>
                        ))}
                        {isCountryError && <DropdownOption disabled>{'status' in countryError ? `Error: ${countryError.status}` : countryError.message || 'Error loading countries'}</DropdownOption>}
                    </Dropdown>}
                </div>
                <div className='div-market-dropdown'>
                    <style>{`
                        .div-market-dropdown vcdk-dropdown::part(input-wrapper) {
                            height: 36px;
                            overflow-y: auto;
                        }`}
                    </style>
                    <Dropdown size='small' label='Market' onChange={onMarketChanged} multi={true}>
                        {marketList.length > 0 ? <><DropdownSelectAllOption>Select All</DropdownSelectAllOption><Divider /></> : null}
                        {marketList.map((company: MarketCompany) => (
                            <DropdownOption key={company.marketCompanyID} value={company.marketCompanyID.toString()}>{company.name}</DropdownOption>
                        ))}
                    </Dropdown>
                </div>
                <div style={{ height: 384, display: 'grid', gap: 4 }}>
                    <Typography variant="subtitle2" style={{ color: "#575757" }}>Accountable type</Typography>
                    <ClassTreeView ref={refClassTreeView} classChangedCallback={classChangedCallback} typeChangedCallback={typeChangedCallback} />
                </div>
                <EntityDropdown />
                <ContractDropdown ref={refContractDropdown} />
            </div>
            <div style={{ flex: 1, gap: 8, display: 'grid', paddingTop: 18 }}>
                <SearchField onInput={onSearch} onClickButton={onClearSearch} size="small" placeholder="Search" icon='search' label={`Dealers${dealerData.dealerList && dealerData.dealerList.length > 0 ? ` (${dealerData.dealerList.length})` : ''}`} />
                {
                    dealerData.loading ?
                    <div style={{ border: '1px solid #A9A9A9', height: 628, display: 'grid', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 4, overflowY: 'auto', minWidth: 495, padding: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><Spinner /></div>
                    </div> : dealerData.dealerList === undefined ?
                    <div style={{ border: '1px solid #A9A9A9', height: 628, display: 'grid', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 4, overflowY: 'auto', minWidth: 495, padding: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <InlineMessage variant="info" icon="info">{dealerData.msg == '' ? 'Please select a country and a market to display the dealer list.' : dealerData.msg}</InlineMessage>
                        </div>
                    </div> : 
                    <div style={{ border: '1px solid #A9A9A9', height: 628, backgroundColor: '#FFFFFF', borderRadius: 4, overflowY: 'auto', minWidth: 495, padding: 8 }}>
                        {dealerData.dealerList && <>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox label='Select All' checked={allDealersChecked} disabled={disableAll.disable} onChange={(e: CheckboxChangeEvent) => {
                                    if(!valid) setValid(true);
                                    setDisableAll(prevState => ({...prevState, disable: true, isAll: true}));
                                }} />
                                {disableAll.disable && <div><Spinner size='small' /></div>}
                            </div>
                            <Divider />
                        </>}
                        <CheckboxGroup>
                            {dealerData.dealerList && dealerData.dealerList.map((dealer: Dealer) => (
                                <Checkbox key={dealer.dealerID} checked={dealer.checked === true ? true : false} label={`${dealer.dealerNumber} - ${dealer.name}`} onChange={(e: CheckboxChangeEvent) => onDealerChanged?.(e, dealer.dealerID)} />
                            ))}
                        </CheckboxGroup>
                    </div>
                }
            </div>
        </div>
    </div>);
})